import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';

interface ForceDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  databaseName: string;
  databaseType: string;
}

const ForceDeleteDialog: React.FC<ForceDeleteDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  databaseName,
  databaseType,
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (confirmationText !== databaseName) {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  const isConfirmDisabled = confirmationText !== databaseName || isDeleting;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <CardTitle className="text-lg">Force Delete Database</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isDeleting}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            This action cannot be undone. The database and all its users will be permanently deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Database Info */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm font-medium text-red-800 mb-1">Database to be deleted:</div>
            <div className="text-sm text-red-700">
              <div><strong>Name:</strong> {databaseName}</div>
              <div><strong>Type:</strong> {databaseType}</div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <div className="font-medium mb-1">⚠️ Warning</div>
                <ul className="list-disc list-inside space-y-1">
                  <li>This will permanently delete the database</li>
                  <li>All database users will be removed</li>
                  <li>All data will be lost</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Type <span className="font-mono text-red-600">{databaseName}</span> to confirm:
            </label>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Enter database name to confirm"
              className={confirmationText && confirmationText !== databaseName ? 'border-red-300' : ''}
              disabled={isDeleting}
            />
            {confirmationText && confirmationText !== databaseName && (
              <div className="text-sm text-red-600">
                Database name does not match
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              className="flex-1"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Force Delete
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForceDeleteDialog; 